import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { API_BASE_URL, reviewAPI } from '@/services/api';
import type { SortDir, SortField } from '@/services/api';
import EDeskLayout from '@/features/layout/EDeskLayout';
import EDeskMailboxTable from './EDeskMailboxTable';

// Some tab names are left over from eDesk's own UI, so several map to the same filter.
const STATUS_BY_TAB: Record<string, string> = {
  'Pending Review': 'pending_review',
  'Pre-sales': 'pending_review',
  New: 'pending_review',
  Approved: 'approved',
  'Approved & Ready': 'approved',
  'My tickets': 'approved',
  'To do': 'approved',
  Rejected: 'rejected',
  Mentioned: 'rejected',
  Sent: 'sent',
  'Sent Responses': 'sent',
  Unassigned: 'sent',
  Waiting: 'sent',
};

const CATEGORY_BY_TAB: Record<string, string> = {
  'Customer Inquiries': 'customer_question',
  'Shipping & Delivery': 'shipping_question',
  'Damaged / Defective': 'damaged_defective',
  'Returns & Refunds': 'return_policy',
  'Order Cancellations': 'order_cancellation',
  'Product Questions': 'product_question',
};

const PAGE_SIZE = 50;

export default function MailboxView() {
  const [activeTab, setActiveTab] = useState('Pre-sales');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortField>('received_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // A different filter or order starts again from the first page
  useEffect(() => {
    setPage(1);
  }, [activeTab, selectedPlatform, sortBy, sortDir]);

  // Clicking the current column flips its direction; a new column starts with its natural order
  const handleSort = (field: SortField) => {
    if (field === sortBy) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir(field === 'received_at' ? 'desc' : 'asc');
    }
  };

  useEffect(() => {
    // EventSource cannot send an Authorization header, so the token goes in the query string
    const token = encodeURIComponent(localStorage.getItem('auth_token') || '');
    const events = new EventSource(`${API_BASE_URL}/emails/stream?token=${token}`);
    events.addEventListener('new_email', (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        toast.success(`New email: ${data.subject || '(no subject)'}`, { duration: 4000 });
      } catch (e) {
        console.error('Could not read live update:', e);
      }
      queryClient.invalidateQueries({ queryKey: ['reviewQueue'] });
      queryClient.invalidateQueries({ queryKey: ['reviewStats'] });
    });
    return () => events.close();
  }, [queryClient]);

  const { data: emailsData, isError } = useQuery({
    queryKey: ['reviewQueue', activeTab, searchQuery, selectedPlatform, page, sortBy, sortDir],
    queryFn: async () => {
      const res = await reviewAPI.getReviewQueue({
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        response_status: STATUS_BY_TAB[activeTab],
        category: CATEGORY_BY_TAB[activeTab],
        platform: selectedPlatform !== 'all' ? selectedPlatform : undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      return res.data;
    },
    // Keep the current rows on screen while the next page loads instead of flashing "no tickets"
    placeholderData: keepPreviousData,
    refetchInterval: 2000,
  });

  const emails = isError ? [] : emailsData?.data ?? (Array.isArray(emailsData) ? emailsData : []);
  const total: number = isError ? 0 : emailsData?.total ?? emails.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Emails approved or rejected elsewhere can shrink the list under the current page
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <EDeskLayout
      activeTab={activeTab}
      onTabChange={setActiveTab}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      selectedPlatform={selectedPlatform}
      onPlatformChange={setSelectedPlatform}
      totalCount={total}
      pagination={{ page, pageSize: PAGE_SIZE, totalPages, onPageChange: setPage }}
      sort={{ field: sortBy, dir: sortDir, onSort: handleSort }}
    >
      <EDeskMailboxTable
        key={`${activeTab}-${page}`}
        emails={emails}
        activeTab={activeTab}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        onSelectTicket={(ticket) => navigate(`/review/${ticket.id}`)}
      />
    </EDeskLayout>
  );
}
